import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CalendarClock, FileWarning, Settings2, UserCheck } from "lucide-react";

import OpenWorkQueueCards from "../../components/OpenWorkQueueCards";
import {
  Button,
  EmptyState,
  ErrorState,
  SkeletonRows,
  Surface,
} from "../../../components/ui";
import { businessApi } from "../../../services/api";
import { recruitmentApi } from "../../../services/recruitmentService";
import { useBusinessAuth } from "../../context/BusinessAuthContext";
import HRPageShell from "./components/HRPageShell";

function memberName(member) {
  return member.displayName || member.display_name || member.name || [member.firstName, member.lastName].filter(Boolean).join(" ") || member.email || `Member #${member.id}`;
}

function WorkList({ id, labelledBy, title, subtitle, rows, emptyTitle, emptyDescription, href, metaRender, rowHref }) {
  return (
    <Surface
      as="section"
      id={id}
      role="tabpanel"
      aria-labelledby={labelledBy}
      toolbar={(
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-section-title text-foreground">{title}</h2>
            {subtitle ? <p className="mt-1 text-body-sm text-secondary">{subtitle}</p> : null}
          </div>
          {href ? <Button as={Link} to={href} size="sm" variant="secondary">Open module</Button> : null}
        </div>
      )}
    >
      {!rows.length ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <ul className="divide-y divide-subtle">
          {rows.map((row) => (
            <li key={`${title}-${row.id}`} className="flex min-h-touch flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="truncate text-label text-foreground">{memberName(row)}</p>
                <p className="mt-1 text-body-sm text-secondary">{metaRender ? metaRender(row) : "HR follow-through required"}</p>
              </div>
              {rowHref ? (
                <Button
                  as={Link}
                  to={rowHref(row)}
                  size="sm"
                  variant="secondary"
                  className="w-full border-accent/30 bg-accent-subtle/40 text-accent hover:border-accent hover:bg-accent-subtle sm:w-auto"
                >
                  Open record
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Surface>
  );
}

export default function HROpenWork() {
  const auth = useBusinessAuth();
  const canReadLeave = String(auth?.role || "").toUpperCase() === "OWNER" || ["hr:leave_requests:read", "hr:leaves:read", "hr:leaves:manage"].some((permission) => auth?.hasPermission?.(permission));
  const canReadHiring = String(auth?.role || "").toUpperCase() === "OWNER" || ["hr:employees:read", "members:invite"].some((permission) => auth?.hasPermission?.(permission));
  const [members, setMembers] = useState([]);
  const [pendingLeave, setPendingLeave] = useState([]);
  const [activationQueue, setActivationQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [membersRes, leaveRes, queueRes] = await Promise.all([
        businessApi.getMembers(),
        canReadLeave ? businessApi.getLeaveRequests({ status: "pending", limit: 10 }).catch(() => ({ rows: [] })) : Promise.resolve({ rows: [] }),
        canReadHiring ? recruitmentApi.activationQueue().catch(() => ({ candidates: [] })) : Promise.resolve({ candidates: [] }),
      ]);
      setMembers(Array.isArray(membersRes?.members) ? membersRes.members : Array.isArray(membersRes?.data) ? membersRes.data : []);
      setPendingLeave(Array.isArray(leaveRes?.rows) ? leaveRes.rows : []);
      setActivationQueue(Array.isArray(queueRes?.candidates) ? queueRes.candidates : []);
    } catch {
      setMembers([]);
      setPendingLeave([]);
      setActivationQueue([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [canReadLeave, canReadHiring]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const missingShift = members.filter((member) => member.onboardingApplicable !== false && !member.shiftAssigned);
    const missingCompensation = members.filter((member) => member.onboardingApplicable !== false && !member.compensationConfigured);
    const incompleteRecords = members.filter((member) => member.onboardingApplicable !== false && !["ready_for_review", "approved_for_activation", "activated"].includes(String(member.onboardingStage || "").toLowerCase()));
    const shiftOrCompensation = Array.from(
      new Map([...missingShift, ...missingCompensation].map((member) => [member.id, member])).values(),
    );
    return { missingShift, missingCompensation, shiftOrCompensation, incompleteRecords };
  }, [members]);

  // Owner decision D-B: hiring runs through Recruitment, so the legacy invitation and onboarding-step queues are gone.
  const lanes = [
    ...(canReadHiring ? [{
      id: "activation",
      title: "Ready to Activate",
      description: "Approved candidates waiting for their start date and activation",
      actionLabel: "Review candidates",
      icon: UserCheck,
      tone: "accent",
      count: activationQueue.length,
    }] : []),
    {
      id: "setup",
      title: "Shift / Compensation",
      description: "Employees who are not yet operationally ready for attendance or payroll",
      actionLabel: "Review setup",
      icon: Settings2,
      tone: "warning",
      count: summary.shiftOrCompensation.length,
    },
    {
      id: "records",
      title: "Incomplete Records",
      description: "Workforce records with unresolved setup dependencies",
      actionLabel: "Review records",
      icon: FileWarning,
      tone: "danger",
      count: summary.incompleteRecords.length,
    },
    ...(canReadLeave ? [{
      id: "leave",
      title: "Pending Leave",
      description: "Leave requests waiting for review",
      actionLabel: "Review leave",
      icon: CalendarClock,
      tone: "warning",
      count: pendingLeave.length,
    }] : []),
  ];
  const requestedLane = searchParams.get("lane");
  const activeLane = lanes.some((item) => item.id === requestedLane)
    ? requestedLane
    : lanes.find((item) => item.count > 0)?.id ?? lanes[0].id;
  const panelId = "hr-work-panel";
  const labelledBy = `hr-queue-${activeLane}`;

  return (
    <HRPageShell
      title="HR Open Work"
      subtitle="Candidates ready to activate, shift and compensation gaps, incomplete employee records, and pending leave."
    >
      <div className="space-y-6">
        <OpenWorkQueueCards
          queues={lanes}
          active={activeLane}
          loading={loading}
          onChange={(nextLane) => setSearchParams({ lane: nextLane })}
          idPrefix="hr-queue"
          panelId={panelId}
          label="HR work queues"
          countNoun="people"
        />

        {loading ? (
          <Surface id={panelId} role="tabpanel" aria-label="Loading HR work">
            <SkeletonRows rows={5} columns={3} label="Loading HR work" />
          </Surface>
        ) : null}
        {!loading && loadError ? (
          <Surface id={panelId} role="tabpanel" aria-label="HR work error">
            <ErrorState title="Could not load HR work" description="Please try again." onRetry={load} />
          </Surface>
        ) : null}
        {!loading && !loadError && activeLane === "activation" ? (
          <Surface
            as="section"
            id={panelId}
            role="tabpanel"
            aria-labelledby={labelledBy}
            toolbar={(
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-section-title text-foreground">Ready to Activate</h2>
                  <p className="mt-1 text-body-sm text-secondary">Approved candidates waiting for their start date and activation</p>
                </div>
                <Button as={Link} to="/business/hr/employees?status=awaiting-activation" size="sm" variant="secondary">Open employees</Button>
              </div>
            )}
          >
            {!activationQueue.length ? (
              <EmptyState title="No candidates waiting" description="Approved candidates appear here until HR activates their employment." />
            ) : (
              <ul className="divide-y divide-subtle">
                {activationQueue.slice(0, 10).map((row) => (
                  <li key={row.application_id} className="flex min-h-touch flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <p className="text-label text-foreground">{[row.applicant_first_name, row.applicant_last_name].filter(Boolean).join(" ") || row.applicant_email_normalized}</p>
                    <p className="text-body-sm text-secondary">{row.role_name || row.position_title}{row.business_details?.startDate ? ` · starts ${row.business_details.startDate}` : ""}</p>
                  </li>
                ))}
              </ul>
            )}
          </Surface>
        ) : null}
        {!loading && !loadError && activeLane === "setup" ? (
          <WorkList
            id={panelId}
            labelledBy={labelledBy}
            title="Missing Shift or Compensation Setup"
            subtitle={`Employees who are not yet operationally ready for attendance or payroll · ${summary.missingShift.length} missing shift · ${summary.missingCompensation.length} missing compensation`}
            rows={summary.shiftOrCompensation.slice(0, 10)}
            rowHref={(member) => `/business/hr/employees/${member.id}`}
            href="/business/hr/employees"
            emptyTitle="No shift or compensation gaps"
            emptyDescription="Every applicable employee currently has shift and compensation setup."
            metaRender={(member) => `Shift ${member.shiftAssigned ? "configured" : "missing"} · Compensation ${member.compensationConfigured ? "configured" : "missing"}`}
          />
        ) : null}
        {!loading && !loadError && activeLane === "records" ? (
          <WorkList
            id={panelId}
            labelledBy={labelledBy}
            title="Incomplete Employee Records"
            subtitle="Workforce records with unresolved setup dependencies"
            rows={summary.incompleteRecords.slice(0, 10)}
            rowHref={(member) => `/business/hr/employees/${member.id}`}
            href="/business/hr/employees"
            emptyTitle="No incomplete records"
            emptyDescription="No employee records have unresolved setup dependencies."
            metaRender={(member) => `${member.departmentName || member.department || "No department"} · ${member.businessRoleName || member.roleDisplayName || member.role || "No role"}`}
          />
        ) : null}
        {!loading && !loadError && activeLane === "leave" && canReadLeave ? (
          <Surface
            as="section"
            id={panelId}
            role="tabpanel"
            aria-labelledby={labelledBy}
            toolbar={(
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-section-title text-foreground">Pending Leave Requests</h2>
                <Button as={Link} to="/business/hr/leave" size="sm" variant="secondary">Open leave</Button>
              </div>
            )}
          >
            {!pendingLeave.length ? (
              <EmptyState title="No pending leave requests" description="No leave requests are waiting for review." />
            ) : (
              <ul className="divide-y divide-subtle">
                {pendingLeave.map((row) => (
                  <li key={row.id} className="grid min-h-touch gap-1 px-4 py-3 text-body-sm sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] sm:items-center sm:gap-3">
                    <p className="text-label text-foreground">{row.member_name || row.employee_name || `#${row.business_member_id}`}</p>
                    <p className="text-secondary">{row.leave_type_name || `Type #${row.leave_type_id}`} · {row.start_date?.slice(0, 10)} – {row.end_date?.slice(0, 10)}</p>
                    <span className="text-meta text-tertiary">{row.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </Surface>
        ) : null}
      </div>
    </HRPageShell>
  );
}
